import React, { ChangeEvent } from "react";
import Media from "react-media";
import { Link } from "react-router-dom";
import { V2 } from "../../Api";

import { RouteComponentPropsHistory } from "../../types";
import { combineDateTime, formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName } from "../../Utilities";
import Checkbox from "../Checkbox";

import withTitle from "../withTitle";

interface ISurgeryPendingDocumentSignViewProps extends RouteComponentPropsHistory<any, any> {

}

interface ISurgeryPendingDocumentSignViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;

    surgeries: any[];
    selectedDocuments: { [id: string]: boolean };
    signedDocuments: { [id: string]: boolean };
}

class SurgeryPendingDocumentSignView extends React.Component<ISurgeryPendingDocumentSignViewProps, ISurgeryPendingDocumentSignViewState> {
    constructor(props: Readonly<ISurgeryPendingDocumentSignViewProps>) {
        super(props);

        const state: ISurgeryPendingDocumentSignViewState = {
            isLoading: true,
            isSubmitting: false,

            surgeries: [],
            selectedDocuments: props.location.state?.selectedDocuments || {},
            signedDocuments: {},
        };

        this.state = state;

        this.handleCheckChanged = this.handleCheckChanged.bind(this);
        this.handleSignButtonClick = this.handleSignButtonClick.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>
        }
        else if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">
                    {this.state.isLoading}
                </div>
            </div>
        }

        const signatureCount = Object.keys(this.state.selectedDocuments).filter(a => this.state.selectedDocuments[a]).length;

        return (
            <Media query="only screen and (max-width: 800px)">
                {matches =>
                    <div className="viewer-root">
                        <div>
                            <p style={{ marginTop: "0" }}>Below are documents that require your signature, grouped by case.</p>
                            {signatureCount > 0
                                ? <div className="action-panel-dialog" style={{ marginBottom: "1em" }}>
                                    <button type="button" className="button" disabled={this.state.isSubmitting === true} onClick={this.handleSignButtonClick}>Sign {signatureCount} document(s)</button>
                                </div>
                                : null}
                            {this.state.surgeries.length === 0
                                ? <div className="status-box">You have no documents waiting to be signed.</div>
                                : this.state.surgeries.map((a: any) => <div key={a.id} style={{ marginTop: "1em" }}>
                                    <b>#{a.patientChart.chartNumber} &mdash; {formatName(a.patientChart.name)} on {a.time ? formatDateTimeLocalNumeric(combineDateTime(a.date, a.time)) : formatDateLocalNumeric(a.date)}</b>
                                    <ul style={{ paddingLeft: "32px" }}>
                                        {a.documentSignatures.map((b: any) => <li key={b.documentId} style={{ padding: matches ? "8px 0" : "4px 0" }}>
                                            {!this.state.signedDocuments[b.documentId]
                                                ? <Checkbox fancy={true} fancySize={matches ? "large" : "normal"} id={b.documentId} name={b.documentId} className="white" checked={!!this.state.selectedDocuments[b.documentId]} onChange={this.handleCheckChanged} />
                                                : null}
                                            <Link to={`/surgery/${a.id}/documents/${b.documentId}`} style={{ marginLeft: matches ? "8px" : undefined }}>{b.documentName}</Link>
                                            {this.state.signedDocuments[b.documentId]
                                                ? <span> <i className="icon-check" style={{ fontSize: "1.5em", color: "green", lineHeight: "1rem" }} /> Signed</span>
                                                : null}
                                        </li>)}
                                    </ul>
                                </div>)}
                        </div>
                &nbsp;
                {this.state.isSubmitting === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.isSubmitting === "string"
                                ? <div className="error-text">{this.state.isSubmitting}</div>
                                : null}
                        {signatureCount > 0
                            ? <div className="action-panel-dialog">
                                <button type="button" className="button" disabled={this.state.isSubmitting === true} onClick={this.handleSignButtonClick}>Sign {signatureCount} document(s)</button>
                            </div>
                            : null}
                    </div>
                }
            </Media>
        );
    }

    private async loadData() {
        try {
            const results = await V2.get<any>(`document/pending`);

            // Remove any documents cached in the selectedDocuments object that no longer exist
            const existingDocuments = (results.surgeries as any[])
                .map((a: any) => a.documentSignatures.map((b: any) => b.documentId))
                .flat(1)
                .reduce((prev, curr) => {
                    prev[curr] = true
                    return prev;
                }, {});

            const selectedDocuments = this.state.selectedDocuments;
            for (const key of Object.keys(selectedDocuments)) {
                if (!existingDocuments[key]) {
                    delete selectedDocuments[key];
                }
            }


            this.setState({
                isLoading: false,
                surgeries: results.surgeries,
                selectedDocuments
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load pending documents"
            });
        }
    }

    private handleCheckChanged(e: ChangeEvent<HTMLInputElement>) {
        const selectedDocuments = this.state.selectedDocuments;

        selectedDocuments[e.currentTarget.name] = e.currentTarget.checked;

        this.setState({
            selectedDocuments
        }, () => this.saveStateInHistory());
    }

    private saveStateInHistory() {
        const location = this.props.history.location;
        location.state = {
            selectedDocuments: this.state.selectedDocuments
        };
        this.props.history.replace(location);
    }

    private async handleSignButtonClick() {
        try {
            this.setState({
                isSubmitting: true
            });

            const documentsToSign = [];
            for (const surgery of this.state.surgeries) {
                for (const signature of surgery.documentSignatures) {
                    if (this.state.selectedDocuments[signature.documentId]) {
                        documentsToSign.push({
                            documentId: signature.documentId,
                            signData: {
                                type: 0, // "electronic"
                                kind: signature.kind,
                                fieldId: signature.fieldId
                            }
                        });
                    }
                }
            }

            for (const sign of documentsToSign) {
                await V2.post(`document/${sign.documentId}/signature`, sign.signData);

                this.setState(s => ({
                    signedDocuments: {
                        ...s.signedDocuments,
                        [sign.documentId]: true
                    }
                }));
            }

            this.setState({
                isSubmitting: false,
                selectedDocuments: {}
            }, () => this.saveStateInHistory());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not sign documents"
            });
        }
    }
}

export default withTitle("Documents Requiring Signatures", SurgeryPendingDocumentSignView);
